import React, { useEffect, useRef } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { r, color, radius, motion, shadow } from '../../../theme/tokens';
import { UNDO, DISMISS_TOAST } from '../copy';

/**
 * The single undo toast — §7 of the dashboard handoff.
 *
 * The rules that are easy to get wrong, and why each is here rather than in a
 * `Snackbar`:
 *
 *   - **× only.** MUI's `Snackbar` closes on `clickaway` by default. §7 forbids
 *     it in as many words: "A tap elsewhere on the page must not dismiss it, so
 *     Undo is never lost to a stray tap." A stray tap on a phone is common, and
 *     losing Undo makes the action irreversible in practice — which is the one
 *     property this toast exists to prevent.
 *   - **One at a time.** A new action replaces the current toast rather than
 *     stacking. The parent holds a single toast object; this component just
 *     re-arms its timer when the payload changes.
 *   - **6s.** Then auto-dismiss, and the Undo goes with it — §7: "Undo remains
 *     valid for the toast's lifetime only."
 *   - **`role="status"` + `aria-live="polite"`**, with Undo keyboard-reachable
 *     while visible. It is focused on mount for exactly that reason.
 *   - **No motion under `prefers-reduced-motion`** — appear and disappear
 *     instantly.
 *
 * Position is `fixed` at the viewport bottom. This is the documented exception
 * to the frontend's no-absolute-positioning rule: a toast that scrolls away is
 * not a toast, and §7 pins it to the bottom of the viewport by name. Everything
 * else on this surface is flex or grid.
 */

const DURATION_MS = 6000;
const REDUCED = '@media (prefers-reduced-motion: reduce)';

export default function UndoToast({ toast, onUndo, onDismiss }) {
  const undoRef = useRef(null);

  // Keyed on the toast's identity, not on the object: a replacement toast must
  // restart the 6s, and a re-render must not.
  const toastId = toast?.id;

  useEffect(() => {
    if (!toastId) return undefined;
    const timer = setTimeout(() => onDismiss(), DURATION_MS);
    return () => clearTimeout(timer);
  }, [toastId, onDismiss]);

  useEffect(() => {
    if (toastId && undoRef.current) {
      undoRef.current.focus();
    }
  }, [toastId]);

  if (!toast) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="undo-toast"
      sx={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        // Desktop: 24px inset. Mobile: above the tab bar, so the inset carries
        // the safe-area inset rather than a guessed constant.
        bottom: {
          xs: 'calc(1rem + env(safe-area-inset-bottom, 0rem) + 3.5rem)',
          md: '1.5rem',
        },
        zIndex: (t) => t.zIndex.snackbar,
        display: 'flex',
        alignItems: 'center',
        // §7 fixes the toast's padding but is silent on the internal gaps.
        // They were too tight (Tanu, 2026-08-26): mis-tapping × while reaching
        // for Undo loses the action, and dismissal is the one thing that
        // cannot be undone. The message/Undo gap opens up, and Undo/× gets a
        // wider one still — see the separate gap on the actions group below.
        gap: '1.25rem',
        maxWidth: 'calc(100vw - 2rem)',
        backgroundColor: color.evergreen,
        borderRadius: radius.md,
        boxShadow: shadow.pop,
        padding: '0.875rem 1rem',
        color: color.paper,
        fontSize: '0.875rem',
        fontWeight: 600,
        transition: `opacity ${motion.ui} ${motion.ease}, transform ${motion.ui} ${motion.ease}`,
        [REDUCED]: { transition: 'none' },
      }}
    >
      <Box component="span" sx={{ lineHeight: 1.4 }}>
        {toast.message}
      </Box>

      <Box
        data-testid="undo-toast-actions"
        sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}
      >
      {toast.onUndo !== null && (
        <Button
          ref={undoRef}
          onClick={onUndo}
          disableRipple
          sx={{
            // ≥ 44×44 hit target (§8) without the label looking oversized.
            minWidth: '2.75rem',
            minHeight: '2.75rem',
            padding: '0 0.5rem',
            color: color.softAccent,
            fontSize: '0.875rem',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { color: color.paper, backgroundColor: 'transparent' },
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {UNDO}
        </Button>
      )}

      <IconButton
        onClick={onDismiss}
        aria-label={DISMISS_TOAST}
        sx={{
          minWidth: '2.75rem',
          minHeight: '2.75rem',
          color: color.softAccent,
          '&:hover': { color: color.paper },
          '&:focus-visible': {
            outline: `${r(2)} solid ${color.accent}`,
            outlineOffset: r(2),
          },
          '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
        }}
      >
        <CloseIcon />
      </IconButton>
      </Box>
    </Box>
  );
}
